/**
 * Imports the necessary components and hooks from external libraries and modules.
 * @module React
 * @module useState
 * @module Alert
 * @module Card
 * @module CardBody
 * @module Col
 * @module Container
 * @module Row
 * @module Label
 * @module Link
 * @module useTranslation
 */
import React from "react";
import { Link } from "react-router-dom";
import { Alert, Card, CardBody, Col, Container, Label, Row } from "reactstrap";
//i18n
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";

/**
 * Import the logo and ticket images from the assets folder.
 * @param {{string}} logo - The path to the brand logo image file.
 * @param {{string}} ticket - The path to the ticket image file.
 * @returns None
 */
// import images
import logo from "../../../assets/images/logo_new.svg";
import ticket from "../../../assets/images/ticket.svg";

import "../auth.css";

/**
 * This module imports the HookForm, CustomButton, CheckBox, and TextField components
 * for use in other modules.
 */
import CheckBox from "../../../components/checkBox/checkBox";
import CustomButton from "../../../components/customButton/customButton";
import HookForm from "../../../components/hookForm/hookForm";
import TextField from "../../../components/textField/textField";
import Constant from "../../../util/constant";

/**
 * A functional component that represents a login form.
 * @param {Object} props - The props object containing the necessary data and functions.
 * @returns The JSX elements representing the login form.
 */
const LoginComponent = (props) => {
  /**
   * Initializes the login form component.
   * @param {{object}} props - The props passed to the component.
   * @returns None
   */
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const {
    loginForm,
    onFormSubmit,
    busy,
    dimensions,
    rememberMe,
    setRememberMe,
    setShowPass,
    showPass,
    error,
  } = props;

  return (
    <React.Fragment>
      <Helmet>
        <title>Login | RizzTix Admin Portal</title>
      </Helmet>
      <div className="authPages login" style={{ height: dimensions?.height }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <div className="logoDiv">
                <img src={logo} alt="logo" />
              </div>
              <Card className="overflow-hidden">
                <div className="bg-primary topContainRow">
                  <Col className="col-7">
                    <h5 className="text-primary">{t("Hello_Admin")}</h5>
                    <p className="text-primary">{t("SIGN_IN_TO_CONTINUE")}</p>
                  </Col>
                  <Col className="col-5 d-flex justify-content-end">
                    <img src={ticket} alt="" className="img-fluid" />
                  </Col>
                </div>
                <CardBody className="Card_Body">
                  <div className="p-3">
                    {error && error ? (
                      <Alert color="danger">{error}</Alert>
                    ) : null}

                    <HookForm
                      defaultForm={{}}
                      // init={form => setLoginForm()}
                      className="form-horizontal"
                      onSubmit={(e) => {
                        onFormSubmit(e);
                      }}
                    >
                      {(formMethod) => {
                        return (
                          <>
                            <div className="mb-4">
                              <div className="d-flex">
                                <Label>{t("EMAIL_ADDRESS")}</Label>
                                <Label className="requiredStyle">*</Label>
                              </div>
                              <TextField
                                formMethod={formMethod}
                                rules={loginForm.email.validate}
                                name={loginForm.email.name}
                                errors={formMethod?.formState.errors}
                                autoFocus={true}
                                type="text"
                                placeholder={t("ENTER_EMAIL")}
                                defaultValue={window.sessionStorage.getItem(
                                  Constant.SESSIONSTORAGEKEYS
                                    .RIZZTIX_REMEMBER_ME_EMAIL
                                )}
                                value={window.sessionStorage.getItem(
                                  Constant.SESSIONSTORAGEKEYS
                                    .RIZZTIX_REMEMBER_ME_EMAIL
                                )}
                                iconClass={
                                  typeof formMethod.formState.errors[
                                    loginForm.email.name
                                  ] === "undefined" &&
                                  formMethod.watch(loginForm.email.name)
                                    ? "icon_mail"
                                    : "icon_mail"
                                }
                                iconRightShow={true}
                                autoCapitalize={"none"}
                              />
                            </div>
                            <div className="mb-4">
                              <div className="d-flex">
                                <Label>{t("PASSWORD")}</Label>
                                <Label className="requiredStyle">* </Label>
                              </div>
                              <div
                                className={
                                  typeof formMethod.formState.errors[
                                    loginForm.password.name
                                  ] === "undefined" &&
                                  formMethod.watch(loginForm.password.name)
                                    ? "input-group auth-pass-inputgroup validPass"
                                    : "input-group auth-pass-inputgroup"
                                }
                                // ''
                              >
                                <TextField
                                  formMethod={formMethod}
                                  rules={loginForm.password.validate}
                                  name={loginForm.password.name}
                                  errors={formMethod?.formState.errors}
                                  stripLeadingWhitespace={false}
                                  defaultValue={window.sessionStorage.getItem(
                                    Constant.SESSIONSTORAGEKEYS
                                      .RIZZTIX_REMEMBER_ME_PASSWORD
                                  )}
                                  value={window.sessionStorage.getItem(
                                    Constant.SESSIONSTORAGEKEYS
                                      .RIZZTIX_REMEMBER_ME_PASSWORD
                                  )}
                                  type={showPass ? "text" : "password"}
                                  placeholder={t("PASSWORD")}
                                  onIconClick={() => setShowPass(!showPass)}
                                />
                                <button
                                  tabindex="-1"
                                  onClick={() => setShowPass(!showPass)}
                                  className="btn btn-light "
                                  type="button"
                                  id="password-addon"
                                >
                                  <i
                                    className={
                                      showPass
                                        ? "mdi mdi-eye-outline"
                                        : "mdi mdi-eye-off-outline"
                                    }
                                  ></i>{" "}
                                </button>
                              </div>
                            </div>
                            <div className="form-check">
                              <CheckBox
                                checkboxClass="form-check-input"
                                id="customControlInline"
                                labelTitle={t("REMEMBER_ME")}
                                checkedState={rememberMe}
                                onCheckedChange={(checked) =>
                                  setRememberMe(checked)
                                }
                              ></CheckBox>
                              <div className="forgotLink">
                                <Link to="/forgot-password"></Link>
                              </div>
                            </div>
                            <div className="mt-4 d-grid">
                              <CustomButton
                                className="btn darkBtn btn-block"
                                type={t("submit")}
                                title={t("LOGIN")}
                                disabled={!formMethod?.formState.isValid}
                                loading={busy}
                              />
                            </div>
                            <div className="mt-4 text-center">
                              <Link
                                to="/forgot-password"
                                className="text_green"
                              >
                                <i className="mdi mdi-lock-outline me-1" />{" "}
                                {t("FORGOT_YOUR_PASSWORD")}
                              </Link>
                            </div>
                          </>
                        );
                      }}
                    </HookForm>
                  </div>
                </CardBody>
              </Card>
              <div className="copyRightTextStyle text-center">
                <p>{`@${currentYear} ${t("COPYRIGHTS_BY_SIGN_IN")}`}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LoginComponent;
